import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { collection, doc, addDoc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  MessageSquareWarning, Users, Trash2, AlertTriangle, ChevronDown, ChevronRight,
  Plus, Pencil, ExternalLink, Inbox, Compass, UserPlus,
} from 'lucide-react';

// Status vocabulary — the callable writes "new"; the admin moves it along.
const FEEDBACK_STATUS = [
  { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-800' },
  { value: 'in_progress', label: 'In progress', color: 'bg-purple-100 text-purple-800' },
  { value: 'fixed', label: 'Fixed', color: 'bg-green-100 text-green-800' },
  { value: 'wont_fix', label: "Won't fix", color: 'bg-slate-100 text-slate-600' },
];
const ISSUE_TYPES = ['Not easy to use', 'Link not working', 'Info incorrect', 'Not relevant', 'Something missing', 'Other'];

function statusMeta(v) {
  return FEEDBACK_STATUS.find(s => s.value === (v || 'new')) || FEEDBACK_STATUS[0];
}
function fmtDateTime(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) +
    ' · ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}
function todayIso() {
  // Local YYYY-MM-DD without pulling in a date lib
  const d = new Date();
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
// Normalise an Instagram value to an absolute URL so it never resolves against
// the app's own origin. Applied on save AND on render (covers legacy values):
//   full URL (has scheme)      → kept as-is
//   "@handle" / bare handle    → https://instagram.com/handle
//   has a dot but no scheme    → prepend https:// (e.g. instagram.com/handle)
function normalizeInstagram(raw) {
  const v = (raw || '').trim();
  if (!v) return '';
  if (/^https?:\/\//i.test(v)) return v;
  if (v.startsWith('@')) return `https://instagram.com/${v.slice(1)}`;
  if (!v.includes('.')) return `https://instagram.com/${v}`;
  return `https://${v}`;
}

// ─────────────────────────────────────────────
// Confirm dialog (mirrors the Book Clubs tab)
// ─────────────────────────────────────────────
function ConfirmDialog({ open, onClose, onConfirm, title, message, confirmLabel }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />{title}
          </DialogTitle>
        </DialogHeader>
        <p className="mt-2 text-sm text-slate-600">{message}</p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={onConfirm}>{confirmLabel}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ═════════════════════════════════════════════
// Tab A — Reports
// ═════════════════════════════════════════════
function ReportRow({ report }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(report.adminNotes || '');
  const [saved, setSaved] = useState(false);
  const timer = useRef(null);

  useEffect(() => { setNotes(report.adminNotes || ''); }, [report.adminNotes]);

  const saveStatus = async (value) => {
    try { await updateDoc(doc(db, 'roadmapFeedback', report.id), { status: value, updatedAt: serverTimestamp() }); }
    catch (err) { console.error('Failed to update status:', err.message); }
  };
  const changeNotes = (v) => {
    setNotes(v);
    setSaved(false);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        await updateDoc(doc(db, 'roadmapFeedback', report.id), { adminNotes: v, updatedAt: serverTimestamp() });
        setSaved(true);
      } catch (err) { console.error('Failed to save notes:', err.message); }
    }, 700);
  };

  const meta = statusMeta(report.status);
  const where = report.pageContext || report.pagePath || '—';

  return (
    <>
      <tr
        className="border-b border-slate-50 cursor-pointer transition-colors hover:bg-slate-50 align-top"
        onClick={() => setOpen(o => !o)}
      >
        <td className="px-3 py-3 text-slate-500 whitespace-nowrap">{fmtDateTime(report.createdAt)}</td>
        <td className="px-3 py-3"><Badge className="bg-teal-100 text-teal-800 border-0">{report.issueType}</Badge></td>
        <td className="px-3 py-3 max-w-[200px]">
          <span className="text-slate-700 line-clamp-1">{where}</span>
        </td>
        <td className="px-3 py-3 max-w-[280px]">
          <span className={open ? 'text-slate-700 whitespace-pre-wrap' : 'text-slate-700 line-clamp-1'}>{report.details}</span>
        </td>
        <td className="px-3 py-3 text-slate-500 hidden md:table-cell">{report.email || <span className="text-slate-300">—</span>}</td>
        <td className="px-3 py-3"><Badge className={`${meta.color} border-0`}>{meta.label}</Badge></td>
        <td className="px-3 py-3 text-slate-400">{open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</td>
      </tr>
      {open && (
        <tr className="border-b border-slate-100 bg-slate-50/60">
          <td colSpan={7} className="px-3 py-4">
            <div className="grid gap-4 sm:grid-cols-2" onClick={e => e.stopPropagation()}>
              <div className="space-y-2 text-sm">
                <p className="whitespace-pre-wrap text-slate-700">{report.details}</p>
                <p className="text-xs text-slate-400">Page: {report.pagePath || '—'}</p>
                {report.email && <p className="text-xs text-slate-400">Reporter: {report.email}</p>}
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-slate-600">Status</label>
                  <Select value={report.status || 'new'} onChange={e => saveStatus(e.target.value)} className="mt-1">
                    {FEEDBACK_STATUS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Admin notes</label>
                  <Textarea
                    value={notes}
                    onChange={e => changeNotes(e.target.value)}
                    rows={3}
                    placeholder="Private notes for triage…"
                    className="mt-1"
                  />
                  {saved && <p className="mt-1 text-xs text-teal-600">Saved</p>}
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function ReportsPanel({ feedback, emailFilter, onClearEmailFilter }) {
  const [statusFilter, setStatusFilter] = useState('all');
  const [issueFilter, setIssueFilter] = useState('all');

  const filtered = useMemo(() => {
    return feedback.filter(f => {
      if (statusFilter !== 'all' && (f.status || 'new') !== statusFilter) return false;
      if (issueFilter !== 'all' && f.issueType !== issueFilter) return false;
      if (emailFilter && (f.email || '').toLowerCase() !== emailFilter.toLowerCase()) return false;
      return true;
    });
  }, [feedback, statusFilter, issueFilter, emailFilter]);

  const newCount = feedback.filter(f => (f.status || 'new') === 'new').length;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-slate-500">
          {feedback.length} report{feedback.length !== 1 ? 's' : ''} · {newCount} new
        </p>
        <div className="flex flex-wrap gap-2">
          <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="w-40">
            <option value="all">All statuses</option>
            {FEEDBACK_STATUS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </Select>
          <Select value={issueFilter} onChange={e => setIssueFilter(e.target.value)} className="w-44">
            <option value="all">All issue types</option>
            {ISSUE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </Select>
        </div>
      </div>

      {emailFilter && (
        <div className="flex items-center gap-2 text-sm">
          <Badge className="bg-teal-100 text-teal-800 border-0">Filtered by {emailFilter}</Badge>
          <button onClick={onClearEmailFilter} className="text-xs text-slate-500 underline hover:text-slate-800">Clear</button>
        </div>
      )}

      {filtered.length === 0 ? (
        <Card className="border-slate-200 border-dashed">
          <CardContent className="p-8 text-center">
            <Inbox className="mx-auto mb-3 h-10 w-10 text-slate-300" />
            <p className="text-sm text-slate-500">
              {feedback.length === 0 ? 'No feedback reports yet. They appear here as testers use the “Report a problem” widget.' : 'No reports match your filters.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50 text-left">
                  <th className="px-3 py-3 font-medium text-slate-600">Date</th>
                  <th className="px-3 py-3 font-medium text-slate-600">Issue</th>
                  <th className="px-3 py-3 font-medium text-slate-600">Page / guide</th>
                  <th className="px-3 py-3 font-medium text-slate-600">Details</th>
                  <th className="px-3 py-3 font-medium text-slate-600 hidden md:table-cell">Reporter</th>
                  <th className="px-3 py-3 font-medium text-slate-600">Status</th>
                  <th className="px-3 py-3" />
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => <ReportRow key={r.id} report={r} />)}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════
// Tab B — Testers
// ═════════════════════════════════════════════
const BLANK_TESTER = { name: '', email: '', instagram: '', signedUpDate: '', country: '', comments: '' };

function TesterModal({ open, tester, onClose, onSave }) {
  const [form, setForm] = useState(BLANK_TESTER);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(tester ? { ...BLANK_TESTER, ...tester } : { ...BLANK_TESTER, signedUpDate: todayIso() });
      setErr('');
    }
  }, [open, tester]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { setErr('Name is required.'); return; }
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) { setErr('A valid email is required.'); return; }
    setSaving(true); setErr('');
    try {
      await onSave({
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        instagram: normalizeInstagram(form.instagram),
        signedUpDate: form.signedUpDate || todayIso(),
        country: form.country.trim(),
        comments: form.comments.trim(),
      });
      onClose();
    } catch (e) {
      setErr('Could not save — please try again.');
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{tester ? 'Edit tester' : 'Add tester'}</DialogTitle>
        </DialogHeader>
        <div className="mt-3 space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-600">Name *</label>
            <Input value={form.name} onChange={e => set('name', e.target.value)} className="mt-1" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Email *</label>
            <Input value={form.email} onChange={e => set('email', e.target.value)} className="mt-1" placeholder="you@example.com" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Instagram</label>
            <Input value={form.instagram} onChange={e => set('instagram', e.target.value)} className="mt-1" placeholder="https://instagram.com/…" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Signed-up date</label>
              <Input type="date" value={form.signedUpDate} onChange={e => set('signedUpDate', e.target.value)} className="mt-1" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Country</label>
              <Input value={form.country} onChange={e => set('country', e.target.value)} className="mt-1" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Comments</label>
            <Textarea value={form.comments} onChange={e => set('comments', e.target.value)} rows={2} className="mt-1" />
          </div>
          {err && <p className="text-xs text-red-600">{err}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button size="sm" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TestersPanel({ testers, feedback, onOpenReportsForEmail }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const countFor = useCallback(
    (email) => feedback.filter(f => (f.email || '').toLowerCase() === (email || '').toLowerCase()).length,
    [feedback]
  );

  const sorted = useMemo(
    () => [...testers].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [testers]
  );

  const saveTester = async (data) => {
    if (editing) {
      await updateDoc(doc(db, 'roadmapTesters', editing.id), { ...data, updatedAt: serverTimestamp() });
    } else {
      await addDoc(collection(db, 'roadmapTesters'), { ...data, createdAt: serverTimestamp() });
    }
  };
  const doDelete = async () => {
    if (!confirmDelete) return;
    try { await deleteDoc(doc(db, 'roadmapTesters', confirmDelete.id)); }
    catch (err) { console.error('Failed to delete tester:', err.message); }
    setConfirmDelete(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{testers.length} tester{testers.length !== 1 ? 's' : ''} on the roster</p>
        <Button size="sm" className="gap-1.5" onClick={() => { setEditing(null); setModalOpen(true); }}>
          <Plus className="h-4 w-4" /> Add tester
        </Button>
      </div>

      {sorted.length === 0 ? (
        <Card className="border-slate-200 border-dashed">
          <CardContent className="p-8 text-center">
            <Users className="mx-auto mb-3 h-10 w-10 text-slate-300" />
            <p className="text-sm text-slate-500">No testers yet. Add your beta testers to build the roster.</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50 text-left">
                  <th className="px-3 py-3 font-medium text-slate-600">Name</th>
                  <th className="px-3 py-3 font-medium text-slate-600">Email</th>
                  <th className="px-3 py-3 font-medium text-slate-600 hidden sm:table-cell">Instagram</th>
                  <th className="px-3 py-3 font-medium text-slate-600 hidden md:table-cell">Signed up</th>
                  <th className="px-3 py-3 font-medium text-slate-600 hidden lg:table-cell">Country</th>
                  <th className="px-3 py-3 font-medium text-slate-600">Reports</th>
                  <th className="px-3 py-3" />
                </tr>
              </thead>
              <tbody>
                {sorted.map(t => {
                  const n = countFor(t.email);
                  return (
                    <tr key={t.id} className="border-b border-slate-50 align-top">
                      <td className="px-3 py-3">
                        <p className="font-medium text-slate-900">{t.name}</p>
                        {t.comments && <p className="text-xs text-slate-400 line-clamp-1 max-w-[180px]">{t.comments}</p>}
                      </td>
                      <td className="px-3 py-3 text-slate-600">{t.email}</td>
                      <td className="px-3 py-3 hidden sm:table-cell">
                        {t.instagram
                          ? <a href={normalizeInstagram(t.instagram)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-teal-600 hover:underline">Link <ExternalLink className="h-3 w-3" /></a>
                          : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-3 text-slate-500 hidden md:table-cell">{t.signedUpDate || '—'}</td>
                      <td className="px-3 py-3 text-slate-500 hidden lg:table-cell">{t.country || '—'}</td>
                      <td className="px-3 py-3">
                        <button
                          onClick={() => onOpenReportsForEmail(t.email)}
                          className={`rounded-full px-2 py-0.5 text-xs font-medium ${n > 0 ? 'bg-teal-100 text-teal-800 hover:bg-teal-200' : 'bg-slate-100 text-slate-400'}`}
                          title={n > 0 ? 'View this tester’s reports' : 'No reports'}
                        >
                          {n}
                        </button>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex gap-1">
                          <Button variant="outline" size="sm" className="h-7 px-2" onClick={() => { setEditing(t); setModalOpen(true); }}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="outline" size="sm" className="h-7 px-2 border-red-200 text-red-600 hover:bg-red-50" onClick={() => setConfirmDelete(t)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <TesterModal open={modalOpen} tester={editing} onClose={() => setModalOpen(false)} onSave={saveTester} />
      <ConfirmDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={doDelete}
        title="Delete tester?"
        message={`This will permanently remove ${confirmDelete?.name || 'this tester'} from the roster. This cannot be undone.`}
        confirmLabel="Delete"
      />
    </div>
  );
}

// ═════════════════════════════════════════════
// Main tab — CRM version. Data comes in as props (App.js owns the superadmin
// roadmapFeedback / roadmapTesters subscriptions); this component is pure UI.
// ═════════════════════════════════════════════
export default function RoadmapTestingTab({ feedback = [], testers = [], marketingFeedback = [], signups = [], genreSuggestions = [] }) {
  const [subTab, setSubTab] = useState('reports');
  const [emailFilter, setEmailFilter] = useState('');

  const newCount = feedback.filter(f => (f.status || 'new') === 'new').length;
  const newMarketingCount = marketingFeedback.filter(f => (f.status || 'new') === 'new').length;
  const unseenSignups = signups.filter(s => s.seen !== true).length;

  const openReportsForEmail = (email) => { setEmailFilter(email); setSubTab('reports'); };

  return (
    <div className="space-y-6">
      {/* Sub-tabs */}
      <div className="flex gap-1 rounded-lg bg-slate-100 p-1 w-fit">
        <button
          onClick={() => setSubTab('reports')}
          className={`flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition ${subTab === 'reports' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
        >
          <MessageSquareWarning className="h-4 w-4" /> Reports
          {newCount > 0 && <span className="ml-0.5 rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white">{newCount}</span>}
        </button>
        <button
          onClick={() => setSubTab('testers')}
          className={`flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition ${subTab === 'testers' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
        >
          <Users className="h-4 w-4" /> Testers
          <span className="ml-0.5 text-xs text-slate-400">{testers.length}</span>
        </button>
        <button
          onClick={() => setSubTab('marketing')}
          className={`flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition ${subTab === 'marketing' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
        >
          <Compass className="h-4 w-4" /> Marketing
          {newMarketingCount > 0 && <span className="ml-0.5 rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white">{newMarketingCount}</span>}
        </button>
        <button
          onClick={() => setSubTab('signups')}
          className={`flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition ${subTab === 'signups' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
        >
          <UserPlus className="h-4 w-4" /> Signups
          {unseenSignups > 0 && <span className="ml-0.5 rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white">{unseenSignups}</span>}
        </button>
      </div>

      {subTab === 'reports' && (
        <ReportsPanel
          feedback={feedback}
          emailFilter={emailFilter}
          onClearEmailFilter={() => setEmailFilter('')}
        />
      )}
      {subTab === 'testers' && (
        <TestersPanel
          testers={testers}
          feedback={feedback}
          onOpenReportsForEmail={openReportsForEmail}
        />
      )}
      {subTab === 'marketing' && <MarketingFeedbackPanel feedback={marketingFeedback} />}
      {subTab === 'signups' && (
        <>
          <SignupsPanel signups={signups} />
          <GenreSuggestionsPanel suggestions={genreSuggestions} />
        </>
      )}
    </div>
  );
}

/* ── Genre suggestions ────────────────────────────────────────────────────
 * Genres members typed under q1's "Other". The catalogue only gates on genres
 * it actually carries, so an unmatched answer never eliminates anything — it
 * is captured here instead, to grow the taxonomy from real data rather than
 * guesses. Read-only; the fix is to add the genre to GENRE_OPTIONS and the
 * product catalogue's `Genre fit` column.
 */
function GenreSuggestionsPanel({ suggestions = [] }) {
  if (!suggestions.length) return null;

  // Group identical answers so a genre asked for ten times reads as ten.
  const grouped = Object.values(
    suggestions.reduce((acc, s) => {
      const k = (s.normalised || s.text || '').trim();
      if (!k) return acc;
      acc[k] = acc[k] || { text: s.text, count: 0, latest: s.createdAt };
      acc[k].count += 1;
      return acc;
    }, {})
  ).sort((a, b) => b.count - a.count);

  return (
    <Card className="border-slate-200 mt-4">
      <CardContent className="p-4">
        <h3 className="mb-1 text-sm font-semibold text-slate-700">Genres we don't cover yet</h3>
        <p className="mb-3 text-xs text-slate-500">
          Typed by members under "Other" on the genre question. Adding one means updating
          GENRE_OPTIONS and the catalogue's Genre fit column.
        </p>
        <div className="flex flex-wrap gap-2">
          {grouped.map((g) => (
            <span key={g.text} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-700">
              {g.text}
              {g.count > 1 && <span className="rounded-full bg-slate-200 px-1.5 text-[10px] font-semibold">{g.count}</span>}
            </span>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/* ── Signup notifications ─────────────────────────────────────────────────
 * Trial starts and plan purchases, written server-side by issueTrial and
 * handleRoadmapPurchase. In-app only for now — a daily email digest is
 * backlogged. Read-only apart from `seen`.
 */
const TERM_LABEL = { trial: 'Free trial', '1m': '1 month', '6m': '6 months', '12m': '12 months' };

function SignupsPanel({ signups = [] }) {
  const [typeFilter, setTypeFilter] = useState('All');

  const rows = useMemo(() => (
    typeFilter === 'All' ? signups : signups.filter(s => s.eventType === typeFilter)
  ), [signups, typeFilter]);

  const trials = signups.filter(s => s.eventType === 'trial_started').length;
  const purchases = signups.filter(s => s.eventType === 'plan_purchased').length;
  const unseen = signups.filter(s => s.seen !== true);

  const markSeen = async (row) => {
    try { await updateDoc(doc(db, 'signupNotifications', row.id), { seen: true, seenAt: serverTimestamp() }); }
    catch (err) { console.error('Failed to mark signup seen:', err.message); }
  };

  const markAllSeen = async () => {
    // Sequential rather than batched — the feed is capped at 200 and this runs
    // once in a while; a batch would need chunking for no real gain.
    for (const row of unseen) await markSeen(row);
  };

  if (!signups.length) {
    return (
      <Card className="border-slate-200 border-dashed">
        <CardContent className="p-8 text-center">
          <UserPlus className="mx-auto mb-2 h-8 w-8 text-slate-300" />
          <p className="text-sm text-slate-500">No signups yet.</p>
          <p className="mt-1 text-xs text-slate-400">Trial starts and plan purchases appear here as they happen.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="w-44">
          <option value="All">All events</option>
          <option value="trial_started">Trials started</option>
          <option value="plan_purchased">Plans purchased</option>
        </Select>
        <span className="text-xs text-slate-500">
          <strong className="text-slate-700">{trials}</strong> trial{trials === 1 ? '' : 's'} ·{' '}
          <strong className="text-slate-700">{purchases}</strong> purchase{purchases === 1 ? '' : 's'}
        </span>
        {unseen.length > 0 && (
          <Button variant="outline" size="sm" className="ml-auto text-xs" onClick={markAllSeen}>
            Mark all {unseen.length} as seen
          </Button>
        )}
      </div>

      <Card className="border-slate-200 overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs text-slate-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Email</th>
                  <th className="px-4 py-2 font-medium">Event</th>
                  <th className="px-4 py-2 font-medium">Plan</th>
                  <th className="px-4 py-2 font-medium">When</th>
                  <th className="px-4 py-2 font-medium"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => {
                  const isPurchase = row.eventType === 'plan_purchased';
                  return (
                    <tr key={row.id} className={row.seen === true ? '' : 'bg-blue-50/40'}>
                      <td className="px-4 py-2 font-medium text-slate-800">{row.email || '—'}</td>
                      <td className="px-4 py-2">
                        <Badge className={`border-0 text-[10px] ${isPurchase ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                          {isPurchase ? 'Plan purchased' : 'Trial started'}
                        </Badge>
                      </td>
                      <td className="px-4 py-2 text-xs text-slate-600">
                        {TERM_LABEL[row.term] || row.term || '—'}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-xs text-slate-500">{fmtDateTime(row.createdAt)}</td>
                      <td className="px-4 py-2 text-right">
                        {row.seen !== true && (
                          <button onClick={() => markSeen(row)} className="text-xs font-medium text-teal-600 hover:text-teal-700">
                            Mark seen
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/* ── Marketing Compass feedback ───────────────────────────────────────────
 * The 30-day "did you try these?" responses, written only by
 * submitMarketingFeedback (Admin SDK). Fields follow the workbook's Feedback
 * Loop sheet: tried / outcome / spend / freeText, segmented by genre and stage.
 *
 * Read-only apart from status, which mirrors the roadmap reports workflow.
 */
const OUTCOME_META = {
  helped: { label: 'Helped', color: 'bg-green-100 text-green-800' },
  no_difference: { label: 'No difference', color: 'bg-slate-100 text-slate-600' },
  wasted_money: { label: 'Wasted money', color: 'bg-red-100 text-red-800' },
  too_hard: { label: 'Too hard', color: 'bg-purple-100 text-purple-800' },
};

function MarketingFeedbackPanel({ feedback = [] }) {
  const [outcomeFilter, setOutcomeFilter] = useState('All');

  const rows = useMemo(() => {
    if (outcomeFilter === 'All') return feedback;
    if (outcomeFilter === 'not_tried') return feedback.filter(f => f.tried === false);
    return feedback.filter(f => f.outcome === outcomeFilter);
  }, [feedback, outcomeFilter]);

  // Adoption rate is the headline signal the Feedback Loop sheet is built for.
  const triedCount = feedback.filter(f => f.tried === true).length;
  const adoption = feedback.length ? Math.round((triedCount / feedback.length) * 100) : null;

  const setStatus = async (row, value) => {
    try {
      await updateDoc(doc(db, 'marketingFeedback', row.id), { status: value, updatedAt: serverTimestamp() });
    } catch (err) {
      console.error('Failed to update marketing feedback status:', err.message);
    }
  };

  if (!feedback.length) {
    return (
      <Card className="border-slate-200 border-dashed">
        <CardContent className="p-8 text-center">
          <Inbox className="mx-auto mb-2 h-8 w-8 text-slate-300" />
          <p className="text-sm text-slate-500">No Marketing Compass feedback yet.</p>
          <p className="mt-1 text-xs text-slate-400">
            Prompts appear on a member's hub 30 days after they generate a stack.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={outcomeFilter} onChange={(e) => setOutcomeFilter(e.target.value)} className="w-48">
          <option value="All">All outcomes</option>
          <option value="not_tried">Didn't try it</option>
          {Object.entries(OUTCOME_META).map(([v, m]) => (
            <option key={v} value={v}>{m.label}</option>
          ))}
        </Select>
        {adoption != null && (
          <span className="text-xs text-slate-500">
            Adoption: <strong className="text-slate-700">{adoption}%</strong> tried
            ({triedCount} of {feedback.length} responses)
          </span>
        )}
      </div>

      <Card className="border-slate-200 overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs text-slate-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Product</th>
                  <th className="px-4 py-2 font-medium">Tried</th>
                  <th className="px-4 py-2 font-medium">Outcome</th>
                  <th className="px-4 py-2 font-medium">Spend</th>
                  <th className="px-4 py-2 font-medium">Segment</th>
                  <th className="px-4 py-2 font-medium">Notes</th>
                  <th className="px-4 py-2 font-medium">Answered</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => {
                  const om = OUTCOME_META[row.outcome];
                  return (
                    <tr key={row.id} className="align-top">
                      <td className="px-4 py-2 font-medium text-slate-800">{row.productId}</td>
                      <td className="px-4 py-2">
                        <Badge className={`border-0 text-[10px] ${row.tried ? 'bg-teal-100 text-teal-800' : 'bg-slate-100 text-slate-600'}`}>
                          {row.tried ? 'Yes' : 'No'}
                        </Badge>
                      </td>
                      <td className="px-4 py-2">
                        {om
                          ? <Badge className={`border-0 text-[10px] ${om.color}`}>{om.label}</Badge>
                          : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-4 py-2 text-slate-600">
                        {row.spend != null ? `£${row.spend}` : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-4 py-2 text-xs text-slate-500">
                        {[row.genre, row.stage].filter(Boolean).join(' · ') || '—'}
                      </td>
                      <td className="px-4 py-2 max-w-xs text-xs text-slate-600">
                        {row.freeText || <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-xs text-slate-500">
                        {fmtDateTime(row.answeredAt)}
                      </td>
                      <td className="px-4 py-2">
                        <Select
                          value={row.status || 'new'}
                          onChange={(e) => setStatus(row, e.target.value)}
                          className="w-32 text-xs"
                        >
                          {FEEDBACK_STATUS.map(s => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                          ))}
                        </Select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
